// src/components/review/TriageCallList.js
// MVP da tab "Triage Chamadas" do ReviewWorkbench.
// Lista chamadas(intake_status='pending_triage') com 2 ações:
//   - Negócio → intake_status='auto_business'
//   - Ignorar → intake_status='ignored'
//
// Decisões MVP:
//   - lista flat ordenada por started_at desc (fallback data_chamada)
//   - duração preferida: duration_seconds; fallback duracao_minutos*60
//   - resumo renderiza só se não-NULL
//   - expand "Detalhes": player <audio> quando record_url (UX-5) + transcrição
//     quando houver texto, senão slot "ainda não disponível (G-UX2)" (UX-6)
//   - record_url storage://call-recordings/<arquivo> em bucket PRIVADO →
//     signed URL on-demand no expand (3600s), com cache por id (G-UX1.1);
//     URLs http são usadas como src direto
//   - transcription_status sempre visível como badge na linha de meta
//   - bulk actions via useBulkSelection + BulkActionBar (chunks de 50)
//   - optimistic remove + toast + rollback (single); re-load em falha bulk

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Briefcase, X, ArrowDownLeft, ArrowUpRight, Phone as PhoneIcon,
  Clock, FileText, ChevronDown, ChevronRight, AlertCircle,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import useBulkSelection from '../shared/useBulkSelection';
import BulkActionBar from '../shared/BulkActionBar';
import SelectAllCheckbox from '../shared/SelectAllCheckbox';
import FilterChips from '../shared/FilterChips';

// Fluxo do filtro:
//   Usuário clica "Caroline Vit (25)" → 25 chamadas → SelectAll → BulkActionBar
//   → Ignorar → confirm → 25 ignoradas → filtro volta "Todos", badge atualiza.

const RESUMO_PREVIEW_LIMIT = 200;
const BULK_CHUNK = 50;
const ALL_FILTER = '__all';
const ZERO_DURATION_FILTER = '__zero_duration';
const NO_NAME = 'Sem nome';

// Extrai nome do contato a partir de contact_name_from_file.
// Padrão Android: "Gravação de chamadas NOME_DDMMYY_HHMMSS.m4a"
const FILENAME_RE = /Grava[çc][ãa]o de chamadas (.+?)_\d{6}_\d{6}\.m4a/i;
function extractContactGroup(call) {
  const raw = (call?.contact_name_from_file || '').trim();
  if (!raw) return NO_NAME;
  const m = raw.match(FILENAME_RE);
  if (m && m[1]) return m[1].trim();
  return raw;
}

function isZeroDuration(call) {
  const ds = call?.duration_seconds;
  const dm = call?.duracao_minutos;
  const dsZero = ds == null || ds === 0;
  const dmZero = dm == null || dm === 0;
  return dsZero && dmZero;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function fmtDuration(seconds) {
  if (seconds == null || isNaN(seconds)) return '—';
  const s = Math.max(0, Math.round(Number(seconds)));
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m === 0) return `${rest}s`;
  if (rest === 0) return `${m}min`;
  return `${m}min ${rest}s`;
}

function fmtPhone(raw) {
  if (!raw) return '';
  const d = String(raw).replace(/\D+/g, '');
  if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  if (d.length === 13 && d.startsWith('55')) {
    return `(${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  }
  return raw;
}

const DIRECTION_META = {
  inbound:  { label: 'recebida',    icon: ArrowDownLeft, tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  outbound: { label: 'realizada',   icon: ArrowUpRight,  tone: 'bg-blue-50    text-blue-700    border-blue-200' },
  unknown:  { label: 'desconhecida', icon: PhoneIcon,    tone: 'bg-slate-50   text-slate-600   border-slate-200' },
};

const SIGNED_URL_TTL = 3600; // 1h cobre a sessão de triage

// Cache de signed URLs por id da chamada — evita re-assinar a cada toggle
// do expand (e a cada re-load da lista, já que o cache é de módulo).
const signedUrlCache = new Map();

// Resolve a URL tocável do player. Bucket call-recordings é PRIVADO:
// storage:// exige createSignedUrl; http é usado direto.
async function resolvePlayableUrl(call) {
  if (signedUrlCache.has(call.id)) return { url: signedUrlCache.get(call.id) };
  const raw = call.record_url || '';
  if (!raw.startsWith('storage://')) {
    signedUrlCache.set(call.id, raw);
    return { url: raw };
  }
  const path = raw.replace(/^storage:\/\/call-recordings\//, '');
  const { data, error } = await supabase.storage
    .from('call-recordings')
    .createSignedUrl(path, SIGNED_URL_TTL);
  if (error) return { error: error.message };
  if (!data?.signedUrl) return { error: 'signed URL vazia' };
  signedUrlCache.set(call.id, data.signedUrl);
  return { url: data.signedUrl };
}

// Vocabulário live (UX-6): 12 none, 4 pending, 2 format_unsupported, 0 completed
const TRANSCRIPTION_STATUS_META = {
  none:               { label: 'sem transcrição',       tone: 'bg-slate-100  text-slate-600' },
  pending:            { label: 'transcrição pendente',  tone: 'bg-amber-100  text-amber-800' },
  format_unsupported: { label: 'formato não suportado', tone: 'bg-red-100    text-red-700' },
  completed:          { label: 'transcrita',            tone: 'bg-emerald-100 text-emerald-700' },
};

export default function TriageCallList({ onChange }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER);
  const sel = useBulkSelection();

  // Chips por contato (extraído do filename) + chip especial "0s duração"
  const { filterChips, filteredCalls } = useMemo(() => {
    const contactCounts = new Map();
    let zeroDurationCount = 0;
    for (const c of calls) {
      const g = extractContactGroup(c);
      contactCounts.set(g, (contactCounts.get(g) || 0) + 1);
      if (isZeroDuration(c)) zeroDurationCount += 1;
    }

    const chips = [{ key: ALL_FILTER, label: 'Todos', count: calls.length }];
    if (zeroDurationCount > 0) {
      chips.push({
        key: ZERO_DURATION_FILTER,
        label: '0s duração',
        count: zeroDurationCount,
        color: 'amber',
      });
    }
    // contatos por contagem desc; "Sem nome" sempre por último
    const entries = Array.from(contactCounts.entries()).sort((a, b) => {
      if (a[0] === NO_NAME) return 1;
      if (b[0] === NO_NAME) return -1;
      return b[1] - a[1];
    });
    for (const [name, n] of entries) {
      chips.push({ key: `contact:${name}`, label: name, count: n });
    }

    let filtered;
    if (activeFilter === ALL_FILTER) {
      filtered = calls;
    } else if (activeFilter === ZERO_DURATION_FILTER) {
      filtered = calls.filter(isZeroDuration);
    } else if (activeFilter.startsWith('contact:')) {
      const name = activeFilter.slice('contact:'.length);
      filtered = calls.filter((c) => extractContactGroup(c) === name);
    } else {
      filtered = calls;
    }
    return { filterChips: chips, filteredCalls: filtered };
  }, [calls, activeFilter]);

  const handleFilterChange = (key) => {
    setActiveFilter(key);
    sel.deselectAll();
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('chamadas')
      .select(
        'id, phone, contact_name_from_file, data_chamada, started_at, ' +
        'direction, duracao_minutos, duration_seconds, resumo, ' +
        'transcription_text, transcription_status, tipo, record_url',
      )
      .eq('intake_status', 'pending_triage')
      .order('started_at', { ascending: false, nullsFirst: false })
      .order('data_chamada', { ascending: false });

    if (err) {
      setError(err.message);
      setCalls([]);
    } else {
      setCalls(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = useCallback((message, tone = 'success') => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const decide = useCallback(
    async (call, action) => {
      // action: 'auto_business' | 'ignored'
      setBusyId(call.id);
      const snapshot = calls;
      setCalls((prev) => prev.filter((c) => c.id !== call.id));

      const { error: updErr } = await supabase
        .from('chamadas')
        .update({ intake_status: action })
        .eq('id', call.id);

      setBusyId(null);

      if (updErr) {
        setCalls(snapshot);
        showToast(`Erro ao salvar: ${updErr.message}`, 'error');
        return;
      }

      showToast(
        action === 'auto_business' ? 'Marcada como negócio' : 'Ignorada',
        'success',
      );
      if (typeof onChange === 'function') onChange();
    },
    [calls, onChange, showToast],
  );

  // Bulk classify — chunked, non-optimistic. Throw mantém seleção na BulkActionBar.
  const bulkDecide = async (action) => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;

    for (let i = 0; i < ids.length; i += BULK_CHUNK) {
      const chunk = ids.slice(i, i + BULK_CHUNK);
      const { error: updErr } = await supabase
        .from('chamadas')
        .update({ intake_status: action })
        .in('id', chunk);
      if (updErr) {
        showToast(`Erro ao salvar lote: ${updErr.message}`, 'error');
        await load();
        throw updErr;
      }
    }

    const idSet = new Set(ids);
    setCalls((prev) => prev.filter((c) => !idSet.has(c.id)));
    const noun = ids.length === 1 ? 'chamada' : 'chamadas';
    const verb = action === 'auto_business'
      ? (ids.length === 1 ? 'marcada como negócio' : 'marcadas como negócio')
      : (ids.length === 1 ? 'ignorada' : 'ignoradas');
    showToast(`${ids.length} ${noun} ${verb}`, 'success');
    if (typeof onChange === 'function') onChange();
  };

  const headerLabel = loading
    ? 'Carregando…'
    : `${calls.length} chamada${calls.length === 1 ? '' : 's'} pendente${calls.length === 1 ? '' : 's'}`;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-600 font-medium">{headerLabel}</div>
        {filteredCalls.length > 0 && (
          <SelectAllCheckbox
            totalCount={filteredCalls.length}
            selectedCount={sel.count}
            onSelectAll={() => sel.selectAll(filteredCalls.map((c) => c.id))}
            onDeselectAll={sel.deselectAll}
          />
        )}
      </div>

      <FilterChips chips={filterChips} activeKey={activeFilter} onChange={handleFilterChange} />

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && calls.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <p className="text-sm text-slate-600">Nenhuma chamada pendente para triage.</p>
        </div>
      )}

      {!loading && !error && calls.length > 0 && filteredCalls.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
          <p className="text-xs text-slate-500">
            Nenhuma chamada neste filtro.{' '}
            <button
              onClick={() => handleFilterChange(ALL_FILTER)}
              className="text-blue-600 hover:underline"
            >
              Limpar filtro
            </button>
          </p>
        </div>
      )}

      {filteredCalls.map((c) => (
        <CallCard
          key={c.id}
          call={c}
          busy={busyId === c.id}
          selected={sel.isSelected(c.id)}
          onToggleSelect={() => sel.toggle(c.id)}
          onBusiness={() => decide(c, 'auto_business')}
          onIgnore={() => decide(c, 'ignored')}
        />
      ))}

      <BulkActionBar
        count={sel.count}
        onClear={sel.deselectAll}
        actions={[
          {
            label: 'Marcar como Negócio',
            icon: Briefcase,
            color: 'blue',
            confirmMessage: (n) => `Classificar ${n} chamada${n === 1 ? '' : 's'} como negócio?`,
            onClick: () => bulkDecide('auto_business'),
          },
          {
            label: 'Ignorar selecionadas',
            icon: X,
            color: 'gray',
            confirmMessage: (n) => `Ignorar ${n} chamada${n === 1 ? '' : 's'}?`,
            onClick: () => bulkDecide('ignored'),
          },
        ]}
      />

      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 px-3 py-2 rounded shadow-lg text-xs font-medium ${
            toast.tone === 'error'
              ? 'bg-red-600 text-white'
              : 'bg-emerald-600 text-white'
          }`}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

function CallCard({ call, busy, selected, onToggleSelect, onBusiness, onIgnore }) {
  const [expandDetails, setExpandDetails] = useState(false);
  const [expandResumo, setExpandResumo] = useState(false);
  // player: idle | loading | ready | error (G-UX1.1 — signed URL on-demand)
  const [audio, setAudio] = useState({ status: 'idle', url: null, error: null });

  const when = call.started_at || call.data_chamada;
  const durationSec = call.duration_seconds != null
    ? call.duration_seconds
    : (call.duracao_minutos != null ? call.duracao_minutos * 60 : null);

  const dir = DIRECTION_META[call.direction] || DIRECTION_META.unknown;
  const DirIcon = dir.icon;

  const resumo = call.resumo || '';
  const resumoTooLong = resumo.length > RESUMO_PREVIEW_LIMIT;
  const resumoView = resumoTooLong && !expandResumo
    ? `${resumo.slice(0, RESUMO_PREVIEW_LIMIT)}…`
    : resumo;

  const hasTranscription = !!call.transcription_text;
  const hasAudio = !!call.record_url;

  const toggleDetails = async () => {
    const opening = !expandDetails;
    setExpandDetails(opening);
    if (!opening || !hasAudio || audio.status === 'ready' || audio.status === 'loading') return;
    setAudio({ status: 'loading', url: null, error: null });
    const res = await resolvePlayableUrl(call);
    if (res.error) setAudio({ status: 'error', url: null, error: res.error });
    else setAudio({ status: 'ready', url: res.url, error: null });
  };

  const transStatus = TRANSCRIPTION_STATUS_META[call.transcription_status]
    || (call.transcription_status
      ? { label: call.transcription_status, tone: 'bg-slate-100 text-slate-600' }
      : null);

  return (
    <div
      className={`bg-white border rounded-lg p-4 transition-opacity ${
        selected ? 'border-blue-400 bg-blue-50/40' : 'border-slate-200'
      } ${busy ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggleSelect}
          aria-label="Selecionar chamada"
          className="cursor-pointer w-5 h-5 mt-0.5 accent-blue-600 flex-shrink-0 rounded"
        />
        <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {call.contact_name_from_file && (
              <span className="font-semibold text-sm text-slate-900 truncate">
                {call.contact_name_from_file}
              </span>
            )}
            <span className="text-[11px] text-slate-500 font-mono">
              {fmtPhone(call.phone)}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${dir.tone}`}
            >
              <DirIcon size={10} /> {dir.label}
            </span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700">
              <Clock size={10} /> {fmtDuration(durationSec)}
            </span>
            <span className="text-[11px] text-slate-500">{fmtDate(when)}</span>
            {transStatus && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${transStatus.tone}`}>
                {transStatus.label}
              </span>
            )}
          </div>
        </div>
      </div>

      {resumo && (
        <div className="mb-3">
          <p className="text-sm text-slate-800 leading-snug whitespace-pre-wrap break-words">
            {resumoView}
          </p>
          {resumoTooLong && (
            <button
              onClick={() => setExpandResumo((v) => !v)}
              className="text-[11px] text-blue-600 hover:underline mt-1"
            >
              {expandResumo ? 'Ver menos' : 'Ver mais'}
            </button>
          )}
        </div>
      )}

      <div className="mb-3">
        <button
          onClick={toggleDetails}
          className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:text-blue-900 font-medium"
        >
          {expandDetails ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <FileText size={11} />
          {expandDetails ? 'Ocultar detalhes' : 'Detalhes'}
          {hasAudio && <span className="text-amber-600">· áudio</span>}
        </button>
        {expandDetails && (
          <div className="mt-2 space-y-2">
            {/* Player inline quando há gravação (UX-5: 6/18 chamadas têm record_url).
                Bucket privado → signed URL resolvida on-demand (G-UX1.1) */}
            {hasAudio && audio.status === 'loading' && (
              <div className="p-2 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-500">
                Resolvendo URL da gravação…
              </div>
            )}
            {hasAudio && audio.status === 'error' && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
                Não foi possível assinar a gravação: {audio.error}
              </div>
            )}
            {hasAudio && audio.status === 'ready' && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio controls preload="none" src={audio.url} className="w-full h-9">
                Seu navegador não suporta áudio embutido.
              </audio>
            )}
            {!hasAudio && (
              <div className="p-2 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-500">
                Sem gravação capturada para esta chamada.
              </div>
            )}
            {hasTranscription ? (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-700 leading-relaxed whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
                {call.transcription_text}
              </div>
            ) : (
              // Slot UX-6: pipeline de transcrição é backend (G-UX2); UI já reserva o lugar
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
                Transcrição ainda não disponível (G-UX2).
              </div>
            )}
          </div>
        )}
      </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button
          onClick={onIgnore}
          disabled={busy}
          className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1.5"
        >
          <X size={13} /> Ignorar
        </button>
        <button
          onClick={onBusiness}
          disabled={busy}
          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
        >
          <Briefcase size={13} /> Negócio
        </button>
      </div>
    </div>
  );
}
